<script setup>
  import { ref, computed, watch } from "vue";
  import { useRoute } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useAppInitStore } from "@stores/appInit";
  import { useCurrentUserStore } from "@stores/currentUser";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useConstantsStore } from "@stores/constants";
  import { useTaskProgressesStore } from "@stores/taskProgresses";
  import { useAiWritingToolStore } from "@stores/aiWritingTool";
  import { Toaster } from "@/components/ui/sonner";
  import { SidebarProvider } from "@/components/ui/sidebar/index.ts";
  import SurveySidebar from "@/components/App/Sidebar/SurveySidebar.vue";
  import urls from "@utils/constants/urls";
  import MainSidebar from "@/components/App/Sidebar/MainSidebar.vue";
  import AdsSidebar from "@/components/App/Sidebar/AdsSidebar.vue";
  import TemplateSidebar from "@/components/App/Sidebar/TemplateSidebar.vue";
  import PricerSidebar from "@/components/App/Sidebar/PricerSidebar.vue";
  import MasterReportSidebar from "@/components/App/Sidebar/MasterReportSidebar.vue";
  import ReportV2Sidebar from "@/components/App/Sidebar/ReportV2Sidebar.vue";
  import NavigationSearch from "@/components/App/NavigationSearch.vue";
  import AssistantHost from "@/components/App/Assistant/AssistantHost.vue";
  import UnsavedChangesDialog from "@/components/App/UnsavedChangesDialog.vue";
  import MCampaignStaleBanner from "@/components/MCampaign/MCampaignStaleBanner.vue";

  const appInitStore = useAppInitStore();
  appInitStore.loadStores();

  useTaskProgressesStore().initializeTaskProgresses();
  useAiWritingToolStore().initialize();

  const route = useRoute();

  const { user, isLoggedIn, isAdmin, isSale, isSuperAdmin } = storeToRefs(useCurrentUserStore());
  const { fetchConstants } = useConstantsStore();

  const surveyStore = useSurveyStore();
  const { survey } = storeToRefs(surveyStore);

  const path = computed(() => route.path);
  const isBlankLayout = computed(() => route.meta.layout === "blank");
  // Dense tool pages can drop the 60px header (breadcrumbs) to give the content the room.
  const hasHeader = computed(() => !route.meta.hideHeader);
  const isErrorPage = computed(() => route.name === "NotFound" || route.name === "Forbidden");
  const isMCampaignPage = computed(() => !isErrorPage.value && urls.ADS.test(path.value));
  const hasSideBar = computed(
    () =>
      !isErrorPage.value &&
      (path.value.match(urls.SURVEY)?.length > 0 ||
        path.value.match(urls.ADS_LIST)?.length > 0 ||
        path.value.match(urls.JOBS)?.length > 0 ||
        path.value.match(urls.JOBS_LIST)?.length > 0 ||
        path.value.match(urls.TEMPLATES)?.length > 0 ||
        path.value.match(urls.TEMPLATE)?.length > 0 ||
        path.value.match(urls.PRICER_PROJECT)?.length > 0 ||
        path.value.match(urls.MASTER_REPORT)?.length > 0 ||
        path.value.match(urls.SURVEY_REPORT_V2)?.length > 0,
      !(isLoggedIn instanceof Error)),
  );
  fetchConstants();
  // The card catalog depends on the user: it is fetched once at boot, before the
  // session is known, so it must be refetched when the user turns out to be an admin.
  watch(isAdmin, () => fetchConstants());

  const open = ref(true);

  const SidebarComponent = computed(() => {
    if (path.value.match(urls.LM_SURVEY)) {
      return null;
    }

    // ReportV2Sidebar must be checked before SurveySidebar since it's a sub-route of survey
    if (path.value.match(urls.SURVEY_REPORT_V2)?.length > 0) {
      return ReportV2Sidebar;
    }

    return path.value.match(urls.ADS)?.length > 0
      ? AdsSidebar
      : path.value.match(urls.SURVEY)?.length > 0
      ? SurveySidebar
      : path.value.match(urls.TEMPLATE)?.length > 0
      ? TemplateSidebar
      : path.value.match(urls.PRICER_PROJECT)?.length > 0
      ? PricerSidebar
      : path.value.match(urls.MASTER_REPORT)?.length > 0
      ? MasterReportSidebar
      : MainSidebar;
  });
</script>

<template>
  <template v-if="isBlankLayout">
    <RouterView />
  </template>
  <template v-else>
    <div
      id="app-container"
      class="flex min-h-screen w-full">
      <SidebarProvider
        v-if="hasSideBar"
        v-model:open="open"
        class="print:hidden">
        <SidebarComponent />
      </SidebarProvider>
      <div class="flex flex-1 flex-col bg-page">
        <header
          v-if="hasHeader"
          class="sticky top-0 z-header bg-page print:hidden">
          <RouterView
            name="nav"
            :user="user"
            :is-logged-in="isLoggedIn"
            :survey="survey"
            :is-admin="isAdmin"
            :is-super-admin="isSuperAdmin"
            :is-sale="isSale" />
        </header>
        <main
          id="main-content"
          class="flex-1 p-5 print:!max-w-none print:bg-white print:px-[4mm] print:py-0"
          :style="open && hasSideBar ? 'max-width: calc(100vw - 240px);' : ''">
          <MCampaignStaleBanner v-if="isMCampaignPage" />
          <RouterView />
        </main>
      </div>
    </div>
    <Toaster />
    <NavigationSearch />
    <AssistantHost />
    <UnsavedChangesDialog />
  </template>
</template>
